import React, { createContext, useContext, useState, useEffect } from 'react';
import { me as authMe, login as authLogin, register as authRegister, logoutAll as authLogoutAll, changePassword as authChangePassword, logout as authLogout, registerStart as authRegisterStart, registerVerify as authRegisterVerify, registerResend as authRegisterResend, emailBindStart, emailBindVerify, resetStart as authResetStart, resetVerify as authResetVerify, resetResend as authResetResend, resetCheck as authResetCheck, resetSet as authResetSet } from '../../shared/api/auth';
import { api, apiFetch } from '../../shared/api/client';
import { THEME_KEY, setTheme } from '../../shared/config/theme';
import { METRIKA_GOALS } from '../../shared/config/metrics';
import { LEGAL_DOCS_VERSION } from '../../shared/config/legalDocs';
import { chatRunStore } from '../../features/chat/chatRunStore';
import { liveChatInputStore } from '../../features/chat/liveChatInputStore';
import { hasLegacyAnalyticsConsent, ANALYTICS_CHOICE_EVENT } from '../../shared/lib/measurementContext';
import { cancelMeasurementEvents } from '../../shared/lib/measurementEvents';
import { setMetrikaUserId, trackGoal, trackGoalAndWait, trackUserParams } from '../../shared/lib/metrika';

const AuthContext = createContext();
const AUTH_REDIRECT_KEY = 'aiDiary.auth.redirectAfterLogin.v1';

async function getJson(path) {
  const response = await apiFetch(path);
  return response.json();
}

function safeInternalRedirect(raw) {
  try {
    const value = String(raw || '').trim();
    if (!value || !value.startsWith('/') || value.startsWith('//') || /[\r\n]/.test(value)) return '';
    const url = new URL(value, 'https://noesa.local');
    if (url.origin !== 'https://noesa.local') return '';
    if (url.pathname === '/login') return '';
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '';
  }
}

function withCacheBust(target) {
  try {
    const path = safeInternalRedirect(target) || '/chat';
    const hashAt = path.indexOf('#');
    const beforeHash = hashAt >= 0 ? path.slice(0, hashAt) : path;
    const hash = hashAt >= 0 ? path.slice(hashAt) : '';
    const queryAt = beforeHash.indexOf('?');
    const pathname = queryAt >= 0 ? beforeHash.slice(0, queryAt) : beforeHash;
    const query = queryAt >= 0 ? beforeHash.slice(queryAt + 1) : '';
    const params = new URLSearchParams(query);
    params.set('ts', String(Date.now()));
    return `${pathname || '/chat'}?${params.toString()}${hash}`;
  } catch {
    return '/chat?ts=' + Date.now();
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const AUTH_STORAGE_KEY = 'ai_diary_auth';
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsEmailBind, setNeedsEmailBind] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      chatRunStore.setOwner(user?.username || '');
      liveChatInputStore.setOwner(user?.username || '');
    }
  }, [isLoading, user?.username]);


  // Device synchronization is owned by AppShell; it preserves manual overrides.

  const [measurementRevision, setMeasurementRevision] = useState(0);
  useEffect(() => {
    const update = () => setMeasurementRevision((v) => v + 1);
    window.addEventListener(ANALYTICS_CHOICE_EVENT, update);
    return () => window.removeEventListener(ANALYTICS_CHOICE_EVENT, update);
  }, []);
  useEffect(() => {
    // Do not broaden third-party identity linking when enabling first-party events.
    if (!isAuthenticated || !user || !hasLegacyAnalyticsConsent()) return;
    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1500);
    (async () => {
      try {
        const response = await apiFetch('/measurement/account', { method: 'POST', signal: controller.signal, suppressAuthError: true });
        const result = await response.json();
        if (cancelled || !hasLegacyAnalyticsConsent() || !/^[a-f0-9]{64}$/.test(result?.marketing_id || '')) return;
        setMetrikaUserId(result.marketing_id);
        trackUserParams({ auth_state: 'user' });
      } catch { /* Measurement must never block login or expose identifiers. */ }
      finally { clearTimeout(timeout); }
    })();
    return () => { cancelled = true; clearTimeout(timeout); controller.abort(); };
  }, [isAuthenticated, user?.username, measurementRevision]);

  const clearAppCaches = () => {
    cancelMeasurementEvents();
    try {
      const username = (() => { try { return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || '{}')?.user?.username || ''; } catch { return ''; } })();
      const su = username ? `.${username}` : '';
      // Feed cache
      try { localStorage.removeItem(`aiDiary.feed.cache.v1${su}`); } catch {}
      try { localStorage.setItem(`aiDiary.feed.dirty.v1${su}`, '1'); } catch {}
      // Analytics caches (per user)
      try { localStorage.removeItem(`aiDiary.analytics.stats${su}`); } catch {}
      try { localStorage.removeItem(`aiDiary.analytics.streak${su}`); } catch {}
      for (const r of ['week','month']) {
        try { localStorage.removeItem(`aiDiary.analytics.mood.${r}${su}`); } catch {}
        try { localStorage.removeItem(`aiDiary.analytics.topics.${r}${su}`); } catch {}
        try { localStorage.removeItem(`aiDiary.analytics.activity.${r}${su}`); } catch {}
        try { localStorage.removeItem(`aiDiary.analytics.calOverview.${r}${su}`); } catch {}
        try { localStorage.removeItem(`aiDiary.analytics.calActivity.${r}${su}`); } catch {}
        try { localStorage.removeItem(`aiDiary.analytics.calHours.${r}${su}`); } catch {}
        try { localStorage.removeItem(`aiDiary.analytics.calTM.${r}${su}`); } catch {}
      }
      // Chat cache per user
      try { localStorage.removeItem(`aiDiary.chat.history.v1${su}`); } catch {}
      // Broadcast
      try { window.dispatchEvent(new CustomEvent('aiDiary:authChanged')); } catch {}
    } catch {}
  };

  const hardReloadApp = () => {
    try {
      // Ensure a full reload and bypass caches with a cache-busting query
      const host = String(window.location?.hostname || '').trim().toLowerCase();
      const storedRedirect = host === 'admin.noesa.ai'
        ? ''
        : safeInternalRedirect(sessionStorage.getItem(AUTH_REDIRECT_KEY) || '');
      if (storedRedirect) {
        try { sessionStorage.removeItem(AUTH_REDIRECT_KEY); } catch {}
      }
      const target = host === 'admin.noesa.ai'
        ? '/?ts=' + Date.now()
        : withCacheBust(storedRedirect || '/chat');
      window.location.assign(target);
    } catch {
      try { window.location.reload(); } catch {}
    }
  };

  // Проверка аутентификации при загрузке
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Если сеть/сервер временно недоступны — не "сжигаем" локальную сессию.
    // Иначе пользователю приходится логиниться заново из-за transient ошибок.
    let savedUser = null;
    let hadLocalSession = false;
    try {
      setIsLoading(true);
      
      // Проверяем сохраненную сессию
      const savedAuthRaw = localStorage.getItem(AUTH_STORAGE_KEY);
      const savedAuth = (() => { try { return savedAuthRaw ? JSON.parse(savedAuthRaw) : null; } catch { return null; } })();
      if (savedAuth?.user) {
        savedUser = savedAuth.user;
        hadLocalSession = true;
        setUser(savedAuth.user);
        setIsAuthenticated(true);
      }
      
      // Проверяем валидность токена на сервере (без принудительного logout)
      const userData = await authMe();
      if (userData && userData.username) {
        // Сохраняем username, но НЕ теряем bearer-токен из localStorage
        const mergedUser = { ...(savedAuth?.user || {}), ...userData };
        setUser(mergedUser);
        setIsAuthenticated(true);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: mergedUser }));
        try {
          // Узнаём статус email на сервере
          const s = await getJson('/settings');
          const verified = !!s?.email_verified;
          const hasEmail = !!s?.email;
          setNeedsEmailBind(!(hasEmail && verified) || !!s?.bind_required);
          // Обновим локального пользователя признаком email_verified
          try {
            const emailVal = (s?.email || '').trim() || (mergedUser?.username || '');
            const merged2 = { ...mergedUser, username: emailVal, email_verified: verified };
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: merged2 }));
            setUser(merged2);
          } catch {}
        } catch {}
      } else {
        // Токен невалиден — просто чистим локальное состояние, без вызова logout_all
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        clearAppCaches();
      }
    } catch (error) {
      const msg = String(error?.message || '');
      const isUnauthorized = msg === 'Unauthorized' || /unauthori[sz]ed/i.test(msg) || error?.status === 401;

      // A guest receives 401 from /auth/me by design. Keep that expected state
      // out of the application error log while retaining diagnostics for real
      // network/server failures.
      if (!isUnauthorized) console.error('Auth check failed:', error);

      // Если токен точно невалиден — чистим. Если это сеть/5xx — оставляем локальную сессию.
      if (isUnauthorized) {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        clearAppCaches();
      } else if (hadLocalSession && savedUser) {
        // Оставляем локальную сессию, чтобы пользователь не логинился заново
        setUser(savedUser);
        setIsAuthenticated(true);
      } else {
        // Нет локальной сессии — показываем гостя, но ничего не "ломаем" дополнительно
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      setIsLoading(true);
      
      const data = await authLogin({ username, password });
      if (data) {
        setUser(data);
        setIsAuthenticated(true);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: data }));
        try {
          // Узнаём статус email на сервере
          const s = await getJson('/settings');
          const verified = !!s?.email_verified;
          const hasEmail = !!s?.email;
          setNeedsEmailBind(!(hasEmail && verified) || !!s?.bind_required);
          try {
            const emailVal = (s?.email || '').trim() || (data?.username || '');
            const merged2 = { ...data, username: emailVal, email_verified: verified };
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: merged2 }));
            setUser(merged2);
          } catch {}
        } catch {}
        // Если есть отложенное согласие — отправим его на RU
        try {
          const pendingStr = localStorage.getItem('aiDiary.consent.pending');
          if (pendingStr) {
            const { giveConsent } = await import('../../shared/api/consents');
            const kinds = pendingStr.split(',').filter(Boolean);
            for (const k of kinds) {
              const ver = (k === 'pd_processing' || k === 'user_agreement' || k === 'offer_subscription')
                ? LEGAL_DOCS_VERSION
                : 'v1';
              try { await giveConsent({ kind: k, granted: true, version: ver }); } catch {}
            }
            localStorage.removeItem('aiDiary.consent.pending');
          }
        } catch {}
        // Устанавливаем светлую тему по умолчанию, если пользователь ещё не выбирал
        try { if (!localStorage.getItem(THEME_KEY)) setTheme('auto'); } catch {}
        // Track auth success before hard reload, otherwise the goal can be lost on navigation.
        try { await trackGoalAndWait(METRIKA_GOALS.login_success, null, { timeoutMs: 650 }); } catch {}
        // Очистка кэшей и принудительная перезагрузка приложения после входа
        clearAppCaches();
        setTimeout(hardReloadApp, 0);
        return { success: true };
      } else {
        return { success: false, error: data.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Legacy username registration kept for NL-only fallback
  const register = async (username, password) => {
    try {
      setIsLoading(true);
      const data = await authRegister({ username, password });
      if (data) {
        setUser(data);
        setIsAuthenticated(true);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: data }));
        try { setTheme('auto'); } catch {}
        // Track registration success before hard reload.
        if (data.account_created === true) {
          try { await trackGoalAndWait(METRIKA_GOALS.registration_verified, null, { timeoutMs: 650 }); } catch {}
        }
        clearAppCaches();
        setTimeout(hardReloadApp, 0);
        return { success: true };
      }
      return { success: false, error: data?.detail || 'Registration failed' };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Email code registration flow (RU backend)
  const startEmailRegistration = async (email, password) => {
    try {
      setIsLoading(true);
      const resp = await authRegisterStart({ email, password });
      try { trackGoal(METRIKA_GOALS.registration_started); } catch {}
      return { success: true, data: resp };
    } catch (error) {
      try { trackGoal(METRIKA_GOALS.registration_error, { step: 'start', reason: error?.message || 'unknown' }); } catch {}
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmailRegistration = async (email, code) => {
    try {
      setIsLoading(true);
      const data = await authRegisterVerify({ email, code });
      if (data && data.username) {
        setUser(data);
        setIsAuthenticated(true);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: data }));
        try { if (!localStorage.getItem(THEME_KEY)) setTheme('auto'); } catch {}
        // Если есть отложенное согласие — отправим его на RU
        try {
          const pendingStr = localStorage.getItem('aiDiary.consent.pending');
          if (pendingStr) {
            const { giveConsent } = await import('../../shared/api/consents');
            const kinds = pendingStr.split(',').filter(Boolean);
            for (const k of kinds) {
              const ver = (k === 'pd_processing' || k === 'user_agreement' || k === 'offer_subscription')
                ? LEGAL_DOCS_VERSION
                : 'v1';
              try { await giveConsent({ kind: k, granted: true, version: ver }); } catch {}
            }
            localStorage.removeItem('aiDiary.consent.pending');
          }
        } catch {}
        // Track registration success before hard reload (verification ends with reload).
        if (data.account_created === true) {
          try { await trackGoalAndWait(METRIKA_GOALS.registration_verified, null, { timeoutMs: 650 }); } catch {}
        }
        clearAppCaches();
        setTimeout(hardReloadApp, 0);
        return { success: true };
      }
      return { success: false, error: 'Verification failed' };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const resendEmailCode = async (email) => {
    try {
      setIsLoading(true);
      await authRegisterResend({ email });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Вызываем logout текущей сессии
      await authLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Очищаем локальное состояние
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      clearAppCaches();
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      setIsLoading(true);
      
      const resp = await authChangePassword({ current_password: oldPassword, new_password: newPassword });
      if (resp && resp.message) {
        return { success: true };
      } else {
        return { success: false, error: 'Password change failed' };
      }
    } catch (error) {
      console.error('Password change error:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Password reset (email code)
  const startPasswordReset = async (email) => {
    try {
      setIsLoading(true);
      const resp = await authResetStart({ email });
      return { success: true, data: resp };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPasswordReset = async (email, code) => {
    try {
      setIsLoading(true);
      const data = await authResetCheck({ email, code });
      if (data && data.reset_token) {
        return { success: true, resetToken: data.reset_token };
      }
      return { success: false, error: 'Reset code check failed' };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const setNewPasswordWithToken = async (resetToken, newPassword) => {
    try {
      setIsLoading(true);
      const data = await authResetSet({ reset_token: resetToken, new_password: newPassword });
      if (data && data.username) {
        // Sync username to email from server settings
        let merged = data;
        try {
          const s = await getJson('/settings');
          const emailVal = (s?.email || '').trim() || (data?.username || '');
          const verified = !!s?.email_verified;
          merged = { ...data, username: emailVal, email_verified: verified };
        } catch {}
        setUser(merged);
        setIsAuthenticated(true);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: merged }));
        try { if (!localStorage.getItem(THEME_KEY)) setTheme('auto'); } catch {}
        clearAppCaches();
        setTimeout(hardReloadApp, 0);
        return { success: true };
      }
      return { success: false, error: 'Password set failed' };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const resendPasswordReset = async (email) => {
    try {
      setIsLoading(true);
      await authResetResend({ email });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    needsEmailBind,
    login,
    register,
    logout,
    changePassword,
    checkAuth,
    // Email registration helpers
    startEmailRegistration,
    verifyEmailRegistration,
    resendEmailCode,
    // Bind email helpers
    emailBindStart,
    emailBindVerify,
    // Password reset helpers
    startPasswordReset,
    verifyPasswordReset,
    resendPasswordReset,
    setNewPasswordWithToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
